import React, { useState, useEffect } from "react";
import {
  AiOutlineRotateLeft,
  AiOutlineRotateRight,
  AiOutlineDownload,
  AiOutlineClose,
} from "react-icons/ai";
import { RiErrorWarningLine } from "react-icons/ri";
import { Modal, Checkbox } from "@shopify/polaris";
import { downloadImage } from "utils/downloadImage";
import "./imageViewer.css";

export function buildPhotos(check) {
  const photos = [];

  if (check.idPhoto) {
    const matchedFaces = check?.inferences?.faceMatch?.FaceMatches || [];
    const unmatchedFaces = check?.inferences?.faceMatch?.UnmatchedFaces || [];
    photos.push({
      type: "Front of ID",
      src: check.idPhoto,
      detailSrc: check.idPhotoDetail,
      matchedFaces,
      unmatchedFaces,
    });
  }

  if (check.backIdPhoto) {
    photos.push({ type: "Back of ID", src: check.backIdPhoto });
  }

  if (check.userPhoto) {
    const photo = { type: "Headshot", src: check.userPhoto };
    const sourceBox =
      check?.inferences?.faceMatch?.SourceImageFace?.BoundingBox;
    if (sourceBox) {
      photo.boundingBox = sourceBox;
    }
    photos.push(photo);
  }

  if (check.signature) {
    photos.push({ type: "E-Signature", src: check.signature });
  }

  if (check.proofOfAddress) {
    photos.push({
      type: "Additional Documentation",
      src: check.proofOfAddress,
    });
  }

  return photos;
}

// Padding-as-percentage of the container width so the rotated image's
// visual overhang doesn't bleed into the controls above / caption below.
// For a landscape image (aspect > 1) rotated 90°/270°, the rotated bounding
// box is taller than the original element by W * (a - 1) / (2a) on each side
// (where W = displayed width, a = naturalWidth / naturalHeight).
function rotationOverflowPercent(aspect, rotation) {
  if (!aspect || aspect <= 1) return 0;
  if (rotation % 180 === 0) return 0;
  return ((aspect - 1) / (2 * aspect)) * 100;
}

function Overlays({ photo }) {
  return (
    <>
      {photo.boundingBox && (
        <div
          className="real-id-image-viewer__bbox"
          style={{
            height: `${photo.boundingBox.Height * 100}%`,
            width: `${photo.boundingBox.Width * 100}%`,
            top: `${photo.boundingBox.Top * 100}%`,
            left: `${photo.boundingBox.Left * 100}%`,
          }}
        />
      )}

      {photo.matchedFaces?.map((row, i) => (
        <div
          key={`match-${i}`}
          className="real-id-image-viewer__bbox real-id-image-viewer__bbox--match"
          style={{
            height: `${row.Face.BoundingBox.Height * 100}%`,
            width: `${row.Face.BoundingBox.Width * 100}%`,
            top: `${row.Face.BoundingBox.Top * 100}%`,
            left: `${row.Face.BoundingBox.Left * 100}%`,
          }}
        />
      ))}

      {photo.unmatchedFaces?.map((face, i) => (
        <React.Fragment key={`unmatch-${i}`}>
          <span className="real-id-image-viewer__mismatch-badge">
            <RiErrorWarningLine />
            Face doesn't match
          </span>
          <div
            className="real-id-image-viewer__bbox real-id-image-viewer__bbox--mismatch"
            style={{
              height: `${face.BoundingBox.Height * 100}%`,
              width: `${face.BoundingBox.Width * 100}%`,
              top: `${face.BoundingBox.Top * 100}%`,
              left: `${face.BoundingBox.Left * 100}%`,
            }}
          />
        </React.Fragment>
      ))}
    </>
  );
}

function DashboardLightbox({ photo, sandboxMode, initialRotation = 0, onClose }) {
  const [rotation, setRotation] = useState(initialRotation);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="real-id-image-viewer__lightbox"
      onClick={onClose}
      role="dialog"
      aria-label={`${photo.type} full size`}
    >
      <button
        type="button"
        aria-label="Close"
        className="real-id-image-viewer__lightbox-close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <AiOutlineClose size={22} />
      </button>
      <div
        className="real-id-image-viewer__lightbox-controls"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Rotate left"
          className="real-id-image-viewer__lightbox-icon-btn"
          onClick={() => setRotation(rotation - 90)}
        >
          <AiOutlineRotateLeft size={22} />
        </button>
        <button
          type="button"
          aria-label="Rotate right"
          className="real-id-image-viewer__lightbox-icon-btn"
          onClick={() => setRotation(rotation + 90)}
        >
          <AiOutlineRotateRight size={22} />
        </button>
      </div>
      <div
        className="real-id-image-viewer__lightbox-frame"
        onClick={(e) => e.stopPropagation()}
        style={{ transform: `rotate(${rotation}deg)` }}
      >
        {sandboxMode && (
          <div className="real-id-image-viewer__sandbox-banner">Test Mode</div>
        )}
        <img
          className="real-id-image-viewer__lightbox-img"
          src={photo.detailSrc || photo.src}
          alt={photo.type}
        />
      </div>
    </div>
  );
}

export function ImagePreview({
  idx,
  photo,
  rotation,
  setRotation,
  openPhoto,
  sandboxMode,
  onRequestDownload,
  downloadAcknowledged,
}) {
  const [imgAspect, setImgAspect] = useState(null);

  const overflow = rotationOverflowPercent(imgAspect, rotation);
  const wrapperStyle = overflow > 0
    ? { paddingTop: `${overflow}%`, paddingBottom: `${overflow}%` }
    : undefined;

  const handleDownload = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (downloadAcknowledged) {
      downloadImage(photo.src);
    } else {
      onRequestDownload(photo);
    }
  };

  return (
    <div className="real-id-image-viewer__photo">
      <div className="real-id-image-viewer__controls">
        <button
          type="button"
          aria-label="Download photo"
          className="real-id-image-viewer__icon-btn real-id-image-viewer__icon-btn--download"
          onClick={handleDownload}
        >
          <AiOutlineDownload size={20} />
        </button>
        <button
          type="button"
          aria-label="Rotate left"
          className="real-id-image-viewer__icon-btn"
          onClick={() => setRotation(rotation - 90)}
        >
          <AiOutlineRotateLeft size={20} />
        </button>
        <button
          type="button"
          aria-label="Rotate right"
          className="real-id-image-viewer__icon-btn"
          onClick={() => setRotation(rotation + 90)}
        >
          <AiOutlineRotateRight size={20} />
        </button>
      </div>
      <div style={wrapperStyle}>
        <a
          className="real-id-image-viewer__frame"
          style={{ transform: `rotate(${rotation}deg)` }}
          href={photo.detailSrc || photo.src}
          onClick={(e) => openPhoto(e, idx, photo.type)}
        >
          <Overlays photo={photo} />
          {sandboxMode && (
            <div className="real-id-image-viewer__sandbox-banner">Test Mode</div>
          )}
          <img
            className="real-id-image-viewer__img"
            src={photo.detailSrc || photo.src}
            alt={photo.type}
            onLoad={(e) =>
              setImgAspect(
                e.currentTarget.naturalWidth / e.currentTarget.naturalHeight
              )
            }
          />
        </a>
      </div>
      <figcaption className="real-id-image-viewer__caption">
        {photo.type}
      </figcaption>
    </div>
  );
}

export default function ImageViewer({ check }) {
  const [open, setOpen] = useState(false);
  const [activePhoto, setActivePhoto] = useState(null);
  const [rotations, setRotations] = useState({});
  const [
    showLiabilityAcknowledgementModal,
    setShowLiabilityAcknowledgementModal,
  ] = useState(false);
  const [acceptLiability, setAcceptLiability] = useState(false);
  // Session-local acknowledgement. The WC plugin authenticates the merchant via
  // license key, not per-user, so there is no equivalent of Shopify's
  // currentUser.downloadLiabilityAcknowledgedAt to persist against.
  const [downloadAcknowledged, setDownloadAcknowledged] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);

  const photos = buildPhotos(check);

  const getRotation = (i) => rotations[i] || 0;
  const setRotationFor = (i, value) =>
    setRotations((prev) => ({ ...prev, [i]: value }));

  const openPhoto = (e, index, type) => {
    e.preventDefault();
    setActivePhoto({ index, type, rotation: getRotation(index) });
    setOpen(true);
  };

  const onRequestDownload = (photo) => {
    setPendingDownload(photo);
    setShowLiabilityAcknowledgementModal(true);
  };

  const acknowledgeAndDownload = () => {
    setDownloadAcknowledged(true);
    setShowLiabilityAcknowledgementModal(false);
    if (pendingDownload) {
      downloadImage(pendingDownload.src);
      setPendingDownload(null);
    }
  };

  if (photos.length === 0) {
    return null;
  }

  return (
    <div className="real-id-image-viewer">
      <h2 className="real-id-image-viewer__heading">
        Submitted photos ({photos.length})
      </h2>

      {photos.map((photo, idx) => (
        <ImagePreview
          key={idx}
          idx={idx}
          photo={photo}
          rotation={getRotation(idx)}
          setRotation={(v) => setRotationFor(idx, v)}
          openPhoto={openPhoto}
          sandboxMode={check.sandboxMode}
          onRequestDownload={onRequestDownload}
          downloadAcknowledged={downloadAcknowledged}
        />
      ))}

      {open && activePhoto && photos[activePhoto.index] && (
        <DashboardLightbox
          photo={photos[activePhoto.index]}
          sandboxMode={check.sandboxMode}
          initialRotation={activePhoto.rotation}
          onClose={() => setOpen(false)}
        />
      )}

      <Modal
        open={showLiabilityAcknowledgementModal}
        title="Acknowledge Liability"
        primaryAction={{
          content: "Accept",
          onAction: acknowledgeAndDownload,
          disabled: !acceptLiability,
        }}
        onClose={() => {
          setShowLiabilityAcknowledgementModal(false);
          setPendingDownload(null);
        }}
      >
        <Modal.Section>
          <div className="real-id-image-viewer__modal-body">
            <p>
              By downloading customer photos, you acknowledge full
              responsibility for securing this data.
            </p>
            <p>
              You also agree to delete the image as required under privacy laws
              like GDPR, CCPA, or other relevant regulations.
            </p>
            <Checkbox
              checked={acceptLiability}
              onChange={(v) => setAcceptLiability(v)}
              label="I acknowledge and accept liability for downloading customer submitted images"
            />
          </div>
        </Modal.Section>
      </Modal>
    </div>
  );
}
